// ============================================================================
// SAMPLE SERVICE SERVICE (Reference Implementation)
// ============================================================================
// Business logic and API calls for Service Master module

import { get, post, put, del } from '../client/axiosClient';
import { sampleServiceEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  ListResponse,
  EmptyResponse,
  BulkOperationResponse,
  ID,
  SampleService,
  CreateSampleServiceRequest,
  UpdateSampleServiceRequest,
  SampleServiceListItem,
  SampleServiceSearchRequest,
  SampleServiceFilterParams,
  SimilarSampleServiceSuggestion,
  SampleServiceValidationResponse,
} from '../types';

/**
 * Service Master Service Class
 */
class SampleServiceService {
  /**
   * Get all services (paginated)
   */
  async getAll(params?: SampleServiceSearchRequest): Promise<PaginatedResponse<SampleServiceListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(sampleServiceEndpoints.list(), params)
      : sampleServiceEndpoints.list();

    const response = await get<PaginatedResponse<SampleServiceListItem>>(endpoint);
    return response.data;
  }

  /**
   * Get service by ID
   */
  async getById(id: ID): Promise<ApiResponse<SampleService>> {
    const response = await get<ApiResponse<SampleService>>(sampleServiceEndpoints.getById(id));
    return response.data;
  }

  /**
   * Create new service
   */
  async create(data: CreateSampleServiceRequest): Promise<ApiResponse<SampleService>> {
    const response = await post<ApiResponse<SampleService>>(
      sampleServiceEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update service
   */
  async update(id: ID, data: UpdateSampleServiceRequest): Promise<ApiResponse<SampleService>> {
    const response = await put<ApiResponse<SampleService>>(
      sampleServiceEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete service
   */
  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(sampleServiceEndpoints.delete(id));
    return response.data;
  }

  /**
   * Search services
   */
  async search(params: SampleServiceSearchRequest): Promise<PaginatedResponse<SampleServiceListItem>> {
    const response = await post<PaginatedResponse<SampleServiceListItem>>(
      sampleServiceEndpoints.search(),
      params
    );
    return response.data;
  }

  /**
   * Get similar service names
   */
  async getSimilarNames(name: string): Promise<ApiResponse<SimilarSampleServiceSuggestion[]>> {
    const response = await get<ApiResponse<SimilarSampleServiceSuggestion[]>>(
      sampleServiceEndpoints.similar(name)
    );
    return response.data;
  }

  /**
   * Validate service data
   */
  async validate(data: CreateSampleServiceRequest): Promise<ApiResponse<SampleServiceValidationResponse>> {
    const response = await post<ApiResponse<SampleServiceValidationResponse>>(
      sampleServiceEndpoints.validate(),
      data
    );
    return response.data;
  }

  /**
   * Bulk create services
   */
  async bulkCreate(services: CreateSampleServiceRequest[]): Promise<BulkOperationResponse> {
    const response = await post<BulkOperationResponse>(
      sampleServiceEndpoints.bulkCreate(),
      { services }
    );
    return response.data;
  }

  /**
   * Bulk update services
   */
  async bulkUpdate(services: UpdateSampleServiceRequest[]): Promise<BulkOperationResponse> {
    const response = await put<BulkOperationResponse>(
      sampleServiceEndpoints.bulkUpdate(),
      { services }
    );
    return response.data;
  }

  /**
   * Bulk delete services
   */
  async bulkDelete(ids: ID[]): Promise<BulkOperationResponse> {
    const response = await del<BulkOperationResponse>(sampleServiceEndpoints.bulkDelete(), {
      data: { ids },
    });
    return response.data;
  }

  /**
   * Export services
   */
  async export(filters?: SampleServiceFilterParams): Promise<Blob> {
    const endpoint = filters
      ? buildEndpointWithQuery(sampleServiceEndpoints.export(), filters)
      : sampleServiceEndpoints.export();

    const response = await get<Blob>(endpoint, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Import services
   */
  async import(file: File): Promise<BulkOperationResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await post<BulkOperationResponse>(
      sampleServiceEndpoints.import(),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  /**
   * Get services by category
   */
  async getByCategory(categoryId: ID): Promise<ListResponse<SampleServiceListItem>> {
    const response = await get<ListResponse<SampleServiceListItem>>(
      sampleServiceEndpoints.byCategory(categoryId)
    );
    return response.data;
  }

  /**
   * Get services by entity
   */
  async getByEntity(entityId: ID): Promise<ListResponse<SampleServiceListItem>> {
    const response = await get<ListResponse<SampleServiceListItem>>(
      sampleServiceEndpoints.byEntity(entityId)
    );
    return response.data;
  }

  /**
   * Get active services
   */
  async getActive(): Promise<ListResponse<SampleServiceListItem>> {
    const response = await get<ListResponse<SampleServiceListItem>>(
      sampleServiceEndpoints.active()
    );
    return response.data;
  }

  /**
   * Get service statistics
   */
  async getStatistics(): Promise<ApiResponse<any>> {
    const response = await get<ApiResponse<any>>(sampleServiceEndpoints.statistics());
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const sampleServiceService = new SampleServiceService();
export default sampleServiceService;
